// =====================================================================
// Employee form — used inside the Add / Edit Employee dialogs.
// Extracted from pages/Employees.tsx without any logic changes.
// =====================================================================

import { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  User,
  Eye,
  EyeOff,
  FileText,
  Briefcase,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Search as SearchIcon,
} from "lucide-react";
import {
  uploadEmployeeDocument,
  getEmployeeDocuments,
  deleteEmployeeDocument,
  type EmployeeDocType,
} from "../../api/employees";
import { useFileDropPaste } from "../../hooks/useFileDropPaste";
import { getCompanyComplianceSettings, getOffice } from "../../api/officeandcompany";
import { useNavigate } from "react-router-dom";
import {
  getManagerList,
  getDepartmentList,
  getDesignationList,
} from "../../redux/slices/employee";
import { User as UserType } from "../../types";
import { useToast } from "../../hooks/useToast";
import { country } from "../../_mock/country";
import { state } from "../../_mock/state";
import { dispatch, useSelector } from "../../redux/store";
import api from "../../api/api";

import {
  PT_STATES,
  LWF_STATES,
  ROLE_OPTIONS_BY_LOGIN_ROLE,
  calcTDS,
  calcPT,
  lwfMonthly,
  fmt,
  num,
  capitalizeFirstLetter,
  splitCtcToComponents,
} from "../../utils/employee.constants";
type DocUploadCardProps = {
  doc: { key: EmployeeDocType; label: string };
  state?: { uploading: boolean; public_url?: string | null; blob_path?: string | null };
  staged: boolean;
  uploaded: boolean;
  isImage: boolean;
  isPdf: boolean;
  pendingName?: string;
  onUpload: (file: File | null) => void;
  onDelete: () => void;
};

function DocUploadCard({
  doc, state, staged, uploaded, isImage, isPdf, pendingName, onUpload, onDelete,
}: DocUploadCardProps) {
  const { dropRef, isDragOver } = useFileDropPaste({
    onFiles: (files) => {
      // Single-file slot — take the first file from a drop or paste.
      if (files[0]) onUpload(files[0]);
    },
    disabled: !!state?.uploading,
  });

  return (
    <div
      ref={dropRef}
      tabIndex={0}
      className={`border rounded-lg p-4 bg-white hover:shadow-sm transition outline-none ${
        isDragOver
          ? "border-blue-400 ring-2 ring-blue-300 ring-offset-1 bg-blue-50/40"
          : "border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-semibold text-gray-800">{doc.label}</Label>
        {uploaded && (
          <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
            Uploaded
          </span>
        )}
        {!uploaded && staged && (
          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
            Staged
          </span>
        )}
      </div>

      {uploaded && state?.public_url && (
        <div className="mb-3">
          {isImage ? (
            <a href={state.public_url} target="_blank" rel="noreferrer">
              <img
                src={state.public_url}
                alt={doc.label}
                className="h-32 w-full object-contain bg-gray-50 border border-gray-200 rounded"
              />
            </a>
          ) : isPdf ? (
            <a
              href={state.public_url}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center justify-center h-32 w-full bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition"
            >
              <span className="text-red-600 text-2xl font-bold">PDF</span>
              <span className="text-xs text-gray-600 mt-1 truncate max-w-full px-2">
                {state.blob_path?.split("/").pop()}
              </span>
            </a>
          ) : (
            <a
              href={state.public_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center h-32 w-full bg-gray-50 border border-gray-200 rounded text-xs text-gray-600 hover:bg-gray-100 transition"
            >
              {state.blob_path?.split("/").pop() || "Open file"}
            </a>
          )}
        </div>
      )}

      <Input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        disabled={state?.uploading}
        onChange={(e) => onUpload(e.target.files?.[0] || null)}
        className="text-xs"
      />
      <p className="text-[10px] text-gray-500 mt-1">
        Drag &amp; drop, click to focus then paste (Ctrl/Cmd+V), or browse. Max size: 1 MB
      </p>

      <div className="flex items-center gap-3 mt-3 min-h-[20px] text-xs">
        {state?.uploading && <span className="text-blue-600">Uploading…</span>}
        {staged && (
          <span className="text-amber-700">{pendingName} — uploads after Save</span>
        )}
        {uploaded && state?.public_url && (
          <a
            href={state.public_url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline"
          >
            View
          </a>
        )}
        {(uploaded || staged) && (
          <button
            type="button"
            onClick={onDelete}
            className="text-red-600 hover:underline"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
export type EmployeeStep = "personal" | "salary" | "documents" | "summary";

export function EmployeeStepsTabNav({
  activeTab,
  onTabChange,
  tabsLocked = false,
  unlockedTabs,
  className = "",
}: {
  activeTab: EmployeeStep;
  onTabChange: (next: EmployeeStep) => void;
  tabsLocked?: boolean;
  unlockedTabs?: EmployeeStep[];
  className?: string;
}) {
  const steps: { id: EmployeeStep; n: number; label: string }[] = [
    { id: "personal", n: 1, label: "Personal Details" },
    { id: "salary", n: 2, label: "Salary & Deductions" },
    { id: "documents", n: 3, label: "Upload Documents" },
    { id: "summary", n: 4, label: "Review & Submit" },
  ];
  const hasPerTabControl = Array.isArray(unlockedTabs);
  const isUnlocked = (id: EmployeeStep): boolean => {
    if (hasPerTabControl) return unlockedTabs!.includes(id);
    return !tabsLocked;
  };
  return (
    <div className={`flex border-b border-gray-200 ${className}`}>
      {steps.map((s) => {
        const unlocked = isUnlocked(s.id);
        const locked = !unlocked;
        return (
          <button
            key={s.id}
            type="button"
            disabled={locked}
            aria-disabled={locked || undefined}
            title={
              locked
                ? hasPerTabControl
                  ? "Complete the previous step to unlock this tab"
                  : "Use the Back/Next buttons to move between steps"
                : undefined
            }
            onClick={() => {
              if (locked) return;
              onTabChange(s.id);
            }}
            className={`px-6 py-3 text-sm font-medium transition-all ${activeTab === s.id
              ? "border-b-2 border-blue-500 text-blue-600"
              : locked
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-500 hover:text-gray-700"
              }`}
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs">{s.n}</span>
              {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function EmployeeForm({
  employee,
  onSubmit,
  currentUserRole,
  initialTab,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  hideTabNav = false,
  existingEmails,
  existingPhones,
  onUnlockedTabsChange,
}: {
  employee?: UserType;
  onSubmit: (data: Partial<UserType>) => void;
  currentUserRole: string;
  initialTab?: EmployeeStep;
  activeTab?: EmployeeStep;
  onActiveTabChange?: (next: EmployeeStep) => void;
  // Hide the form's own tab bar when the parent renders an external one.
  hideTabNav?: boolean;
  existingEmails?: string[];
  existingPhones?: string[];
  onUnlockedTabsChange?: (tabs: EmployeeStep[]) => void;
}) {
  const allowedRoles = ROLE_OPTIONS_BY_LOGIN_ROLE[currentUserRole] || [];
  const [employeeSettings, setSettings] = useState({
    disable_mandatory: false,
    is_active: true,
  });

  const [addDepartmentModal, setAddDepartmentModal] = useState(false);
  const [departmentName, setDepartmentName] = useState("");
  const [officeLocationMissingOpen, setOfficeLocationMissingOpen] =
    useState(false);
  const navigate = useNavigate();
  const officeHasGeofence = async (): Promise<boolean> => {
    try {
      const r = await getOffice();
      const office = r?.offices?.[0];
      if (!office) return false;
      const lat = parseFloat(office.lattitude ?? office.latitude ?? "");
      const lon = parseFloat(office.longitude ?? "");
      const radius = parseFloat(office.radius_meters ?? "");
      return (
        Number.isFinite(lat) && lat !== 0 &&
        Number.isFinite(lon) && lon !== 0 &&
        Number.isFinite(radius) && radius > 0
      );
    } catch {
      return false;
    }
  };
  // Search input state for the in-dropdown filter on Department / Designation pickers.
  const [deptSearch, setDeptSearch] = useState("");
  const [desigSearch, setDesigSearch] = useState("");
  // Search input state for the in-dropdown filter on State (current / permanent address).
  const [currStateSearch, setCurrStateSearch] = useState("");
  const [permStateSearch, setPermStateSearch] = useState("");
  // Search input state for the in-dropdown filter on Country.
  const [countrySearch, setCountrySearch] = useState("");
  const [internalActiveTab, setInternalActiveTab] = useState<EmployeeStep>(initialTab || "personal");
  const isTabControlled =
    controlledActiveTab !== undefined && onActiveTabChange !== undefined;
  const activeTab: EmployeeStep = isTabControlled
    ? (controlledActiveTab as EmployeeStep)
    : internalActiveTab;
  const setActiveTab = (next: EmployeeStep) => {
    if (isTabControlled) {
      onActiveTabChange!(next);
    } else {
      setInternalActiveTab(next);
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Optional employee documents (Aadhar/PAN/CV/Relieving/Form16/Experience) ───
  // Stored on GCS (bucket: teamlio). None of these are required to submit the form.
  const [docsState, setDocsState] = useState<
    Record<EmployeeDocType, { uploading: boolean; public_url?: string | null; blob_path?: string | null }>
  >({
    aadhar:            { uploading: false },
    pan:               { uploading: false },
    cv:                { uploading: false },
    relieving_letter:  { uploading: false },
    form16:            { uploading: false },
    experience_letter: { uploading: false },
  });

  // Files chosen in Add mode (employee not yet created). Uploaded automatically
  // after the employee is saved.
  const [pendingDocs, setPendingDocs] = useState<Partial<Record<EmployeeDocType, File>>>({});

  useEffect(() => {
    if (!employee?.id) return;
    let mounted = true;
    (async () => {
      try {
        const res = await getEmployeeDocuments(employee.id);
        if (!mounted) return;
        const data = res?.data || {};
        setDocsState((prev) => {
          const next = { ...prev };
          (Object.keys(next) as EmployeeDocType[]).forEach((k) => {
            if (data[k]) {
              next[k] = {
                uploading: false,
                public_url: data[k].public_url,
                blob_path: data[k].blob_path,
              };
            }
          });
          return next;
        });
      } catch (err) {
        // documents are optional — fail silently
        console.warn("Could not load employee documents", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [employee?.id]);

  const handleUploadDoc = async (docType: EmployeeDocType, file: File | null) => {
    if (!file) return;

    // Client-side size guard — backend enforces the same 1 MB cap.
    const EMPLOYEE_DOC_MAX_BYTES = 1 * 1024 * 1024;
    if (file.size > EMPLOYEE_DOC_MAX_BYTES) {
      toast({
        title: "File too large",
        description: `${docType.replace("_", " ")} must be 1 MB or smaller.`,
        variant: "destructive",
      });
      return;
    }

    // Add mode → just stage the file, upload after the employee is saved.
    if (!employee?.id) {
      setPendingDocs((prev) => ({ ...prev, [docType]: file }));
      toast({
        title: "Staged",
        description: `${docType.replace("_", " ")} will upload after you save the employee.`,
      });
      return;
    }

    setDocsState((prev) => ({ ...prev, [docType]: { ...prev[docType], uploading: true } }));
    try {
      const res = await uploadEmployeeDocument(employee.id, docType, file);
      setDocsState((prev) => ({
        ...prev,
        [docType]: {
          uploading: false,
          public_url: res?.data?.public_url,
          blob_path: res?.data?.blob_path,
        },
      }));
      toast({ title: "Uploaded", description: `${docType.replace("_", " ")} saved.` });
    } catch (err: any) {
      setDocsState((prev) => ({ ...prev, [docType]: { ...prev[docType], uploading: false } }));
      toast({
        title: "Upload failed",
        description: err?.response?.data?.detail || err?.message || "Could not upload",
        variant: "destructive",
      });
    }
  };

  const handleDeleteDoc = async (docType: EmployeeDocType) => {
    // Add mode — only the staged file exists; just clear it.
    if (!employee?.id) {
      setPendingDocs((prev) => {
        const next = { ...prev };
        delete next[docType];
        return next;
      });
      return;
    }
    try {
      await deleteEmployeeDocument(employee.id, docType);
      setDocsState((prev) => ({
        ...prev,
        [docType]: { uploading: false, public_url: null, blob_path: null },
      }));
      toast({ title: "Removed", description: `${docType.replace("_", " ")} deleted.` });
    } catch (err: any) {
      toast({
        title: "Delete failed",
        description: err?.response?.data?.detail || err?.message || "Could not delete",
        variant: "destructive",
      });
    }
  };
  const [addDesignationModal, setAddDesignationModal] = useState(false);
  const [designationName, setDesignationName] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Salary calculation states
  const [ctcEnabled, setCtcEnabled] = useState(true);
  const [ctcValue, setCtcValue] = useState(0);
  const [showSalaryDetails, setShowSalaryDetails] = useState(false);
  const skipCtcAutoSplitRef = useRef(true);
  const [isYearly, setIsYearly] = useState(true);
  const [ptState, setPtState] = useState(employee?.ptState || "karnataka");
  const [lwfState, setLwfState] = useState(employee?.lwfState || "karnataka");
  const [ptApplicable, setPtApplicable] = useState(false);
  const [lwfApplicable, setLwfApplicable] = useState(false);
  const [pfApplicable, setPfApplicable] = useState(false);
  const [pfBasis, setPfBasis] = useState<"statutory" | "actual">(employee?.pfBasis || "statutory");
  // Employee PF rate (≥12%, employer fixed 12%) and whether employer PF is
  // part of CTC — both sourced from company compliance settings.
  const [pfEmployeeRate, setPfEmployeeRate] = useState<number>((employee as any)?.pfEmployeeRate || 12);
  const [pfFromCtc, setPfFromCtc] = useState(false);
  const [esicApplicable, setEsicApplicable] = useState(false);

  // TDS Declaration state
  const [declaration, setDeclaration] = useState({
    sec80c: employee?.sec80c || 0,
    sec80d: employee?.sec80d || 0,
    sec80ccd1b: employee?.sec80ccd1b || 0,
    hraExempt: employee?.hraExempt || 0,
    homeLoan: employee?.homeLoan || 0,
    ltaExempt: employee?.ltaExempt || 0,
    sec80g: employee?.sec80g || 0,
    npsEmployer: employee?.npsEmployer || 0,
    ptPaid: employee?.ptPaid || 0,
    otherDed: employee?.otherDed || 0,
  });

  // Multiple salary records state
  const [salaryRecords, setSalaryRecords] = useState<any[]>([
    {
      id: Date.now().toString(),
      effectiveDate: employee?.hire_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      status: 'active',
      basicSalary: 0,
      hra: 0,
      conveyance: 0,
      medicalallowance: 0,
      allowance: 0,
      lta: 0,
      others: 0,
      bonusAmount: 0,
      customComponents: [],
      isCurrent: true
    }
  ]);

  const [formData, setFormData] = useState({
    id: employee?.id || "",
    first_name: employee?.first_name || "",
    middle_name: employee?.middle_name || "",
    last_name: employee?.last_name || "",
    gender: employee?.gender || "",
    employeeID: employee?.employeeID || "",
    email: employee?.email || "",
    // Pre-fill with the existing password (the Employee table keeps a
    // readable copy and /employee_by_ID returns it) so the edit panel shows
    // it as dots and the eye toggle can reveal it. Blank only on add.
    password: employee?.password || "",
    phone: employee?.phone || "",
    date_of_birth: employee?.date_of_birth || "",
    hire_date: employee?.hire_date || "",
    department_id: employee?.department_id || "",
    // designation_id: employee?.designation_id || "",
    designation_id: "",
    manager_id: employee?.manager_id || "",
    emergency_contacts: employee?.emergency_contacts || "",
    salary_amount: employee?.salary_amount || 0,
    profile_photo_url: employee?.profile_photo_url || "",
    country: employee?.country || "19",
    current_address1: employee?.current_address1 || "",
    current_address2: employee?.current_address2 || "",
    current_city: employee?.current_city || "",
    current_state: employee?.current_state || "",
    current_pin: employee?.current_pin || "",
    permanent_address1: employee?.permanent_address1 || "",
    permanent_address2: employee?.permanent_address2 || "",
    permanent_city: employee?.permanent_city || "",
    permanent_state: employee?.permanent_state || "",
    permanent_pin: employee?.permanent_pin || "",
    employment_status: employee?.employment_status || "",
    sameAs: employee?.sameAs || false,
    accountNum: employee?.accountNum || "",
    ifscCode: employee?.ifscCode || "",
    bankName: employee?.bankName || "",
    bankbranch: employee?.bankbranch || "",
    swiftCode: employee?.swiftCode || "",
    upiCode: employee?.upiCode || "",
    qrcodeUrl: employee?.qrcodeUrl || "",
    othersBankDetails: employee?.othersBankDetails || "",
    is_active: employee?.is_active ?? true,
    department: employee?.department || "",
    role: employee?.role || "employee",
    netsalary: employee?.netsalary || 0,
    leave: employee?.leave || 0,
    modeOfWorking: employee?.modeOfWorking || "",
    workfromhome: employee?.workfromhome || false,
    workfromhome_list: employee?.workfromhome_list || [],
    taxRegime: employee?.taxRegime || "new",
    tdsApplicable: false,
    TaxConfig: employee?.TaxConfig || [],

  });

  const isMandatoryDisabled = employeeSettings.disable_mandatory;

  useEffect(() => {
    fetchEmployeeSettings();
  }, []);


  const fetchEmployeeSettings = async () => {
    try {
      const res = await api.get("/get_employee_setting");
      // setEmployeeSettings(res.data);
      setSettings({
        disable_mandatory: res.data?.employee_setting?.disable_mandatory ?? false,
        is_active: res.data?.employee_setting?.is_active ?? true,
      });
      console.log("Employee Settings:", res.data);
    } catch (err) {
      console.error("Error fetching employee settings", err);
    }
  };

  // Load existing salary records from employee
  useEffect(() => {
    if (employee?.SalaryDetails && employee.SalaryDetails.length > 0) {
      const records = employee.SalaryDetails.map((detail: any, index: number) => ({
        id: Date.now().toString() + index,
        effectiveDate: detail.effectiveDate || employee.hire_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        status: detail.status || (index === 0 ? 'active' : 'pending'),
        basicSalary: detail.basicsalary || 0,
        hra: detail.hra || 0,
        conveyance: detail.conveyance || 0,
        medicalallowance: detail.medicalallowance || 0,
        allowance: detail.allowance || 0,
        lta: detail.lta || 0,
        others: detail.others || 0,
        bonusAmount: detail.bonus || 0,
        customComponents: detail.customComponents || [],
        isCurrent: index === 0
      }));
      if (records.length > 0) {
        setSalaryRecords(records);

        skipCtcAutoSplitRef.current = true;
        const firstRecord = records[0];
        const details = calculateSalaryDetailsForRecord(firstRecord);
        setCtcValue(Math.round(details.ctc * 12));
      }
    }
  }, [employee]);

  // Update salary record function
  const updateSalaryRecord = useCallback((id: string, field: string, value: any) => {
    setSalaryRecords(prevRecords =>
      prevRecords.map(record =>
        record.id === id ? { ...record, [field]: value } : record
      )
    );
  }, []);

  useEffect(() => {
    if (employee) return; // edit mode → leave existing dates alone
    if (!formData.hire_date) return;
    setSalaryRecords(prev => {
      if (prev.length === 0) return prev;
      
      const [first, ...rest] = prev;
      if (first.effectiveDate === formData.hire_date) return prev;
      return [{ ...first, effectiveDate: formData.hire_date }, ...rest];
    });
  }, [employee, formData.hire_date]);

  // Add new salary record
  const addSalaryRecord = () => {
    setSalaryRecords([
      ...salaryRecords,
      {
        id: Date.now().toString(),
        effectiveDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        basicSalary: 0,
        hra: 0,
        conveyance: 0,
        medicalallowance: 0,
        allowance: 0,
        lta: 0,
        others: 0,
        bonusAmount: 0,
        customComponents: [],
        isCurrent: false
      }
    ]);
  };

  // Remove salary record
  const removeSalaryRecord = (id: string) => {
    if (salaryRecords.length === 1) {
      toast({ // title: "Error",
        description: "At least one salary record is required", variant: "destructive"
      });
      return;
    }
    setSalaryRecords(salaryRecords.filter(record => record.id !== id));
  };

  // Calculate salary details for a specific record
  const calculateSalaryDetailsForRecord = useCallback((record: any) => {
    const basic = num(record.basicSalary);
    const hra = num(record.hra);
    const conv = num(record.conveyance);
    const med = num(record.medicalallowance);
    const special = num(record.allowance);
    const lta = num(record.lta);
    const other = num(record.others);
    const bonus = num(record.bonusAmount);

    const customTotal = record.customComponents?.reduce((sum: number, comp: any) => sum + (comp.amount || 0), 0) || 0;
    const gross = basic + hra + conv + med + special + lta + other + bonus + customTotal;

    let pfBase = pfBasis === "statutory" ? Math.min(basic, 15000) : basic;
    // Employer fixed 12%; employee can raise their rate (never below 12%).
    const pfEE = pfApplicable ? Math.round(pfBase * (pfEmployeeRate / 100)) : 0;
    const pfER = pfApplicable ? Math.round(pfBase * 0.12) : 0;
    const eps = pfApplicable ? Math.round(Math.min(pfBase, 15000) * 0.0833) : 0;
    const edli = pfApplicable ? Math.round(Math.min(pfBase, 15000) * 0.005) : 0;

    const esicEE = (esicApplicable && gross <= 21000) ? Math.round(gross * 0.0075) : 0;
    const esicER = (esicApplicable && gross <= 21000) ? Math.round(gross * 0.0325) : 0;

    const pt = ptApplicable ? calcPT(gross, ptState) : 0;

    const lwfData = lwfMonthly(lwfState);
    const lwfEE = lwfApplicable ? Math.round(lwfData.ee) : 0;
    const lwfER = lwfApplicable ? Math.round(lwfData.er) : 0;

    const grossAnnual = gross * 12;
    const pfEEAnnual = pfEE * 12;
    const pfERAnnual = pfER * 12;
    // Both employee + employer PF reduce taxable income (company policy).
    const tds = formData.tdsApplicable
      ? calcTDS(grossAnnual, formData.taxRegime, declaration, pfEEAnnual, pfERAnnual)
      : 0;

    // Employer PF reduces take-home only when it's included in CTC.
    const employerPfInNet = pfFromCtc ? pfER : 0;
    const totDed = pfEE + esicEE + pt + lwfEE + tds + employerPfInNet;
    const net = gross - totDed;
    const ctc = gross + (pfFromCtc ? 0 : pfER) + esicER + eps + edli + lwfER;

    return { gross, pfEE, pfER, eps, edli, esicEE, esicER, pt, lwfEE, lwfER, tds, totDed, net, ctc, customTotal };
  }, [pfApplicable, pfBasis, pfEmployeeRate, pfFromCtc, esicApplicable, ptApplicable, ptState, lwfApplicable, lwfState, declaration, formData.tdsApplicable, formData.taxRegime]);

  useEffect(() => {
    if (ctcEnabled && ctcValue > 0 && salaryRecords.length > 0) {
      if (skipCtcAutoSplitRef.current) {
        skipCtcAutoSplitRef.current = false;
        return;
      }
      const monthlyCTC = isYearly ? ctcValue / 12 : ctcValue;
      const c = splitCtcToComponents(monthlyCTC);

      updateSalaryRecord(salaryRecords[0].id, 'basicSalary', c.basic);
      updateSalaryRecord(salaryRecords[0].id, 'hra', c.hra);
      updateSalaryRecord(salaryRecords[0].id, 'conveyance', c.conveyance);
      updateSalaryRecord(salaryRecords[0].id, 'medicalallowance', c.medicalallowance);
      updateSalaryRecord(salaryRecords[0].id, 'allowance', c.allowance);
      updateSalaryRecord(salaryRecords[0].id, 'lta', c.lta);
      updateSalaryRecord(salaryRecords[0].id, 'others', c.others);
      updateSalaryRecord(salaryRecords[0].id, 'bonusAmount', c.bonusAmount);
    }
  }, [ctcEnabled, ctcValue, isYearly, salaryRecords.length, updateSalaryRecord]);

  // Calculate CTC from current components
  const calculateCTCFromComponents = useCallback(() => {
    if (salaryRecords.length > 0) {
      const details = calculateSalaryDetailsForRecord(salaryRecords[0]);
      const annualCTC = details.ctc * 12;
      setCtcValue(Math.round(annualCTC));
      setIsYearly(true);
      toast({
        title: "CTC Calculated",
        description: `Annual CTC: ₹${fmt(annualCTC)}`,
      });
    }
  }, [salaryRecords, calculateSalaryDetailsForRecord]);

  useEffect(() => {
    dispatch(getDesignationList());
  }, []);

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  useEffect(() => {
    dispatch(getManagerList(formData.department_id || "all"));
  }, [formData.department_id, formData.role]);

  useEffect(() => {
    if (formData.sameAs) {
      setFormData((prev) => ({
        ...prev,
        permanent_address1: prev.current_address1,
        permanent_address2: prev.current_address2,
        permanent_city: prev.current_city,
        permanent_state: prev.current_state,
        permanent_pin: prev.current_pin,
      }));
    }
  }, [formData.current_address1, formData.current_address2, formData.current_city, formData.current_state, formData.current_pin, formData.sameAs]);

  useEffect(() => {
    if (formData.workfromhome_list.length > 0) {
      setFormData((prev) => ({ ...prev, workfromhome_list: [] }));
    }
  }, [formData.modeOfWorking]);


   useEffect(() => {
    const fetchComplianceSettings = async () => {
      try {
        const response = await getCompanyComplianceSettings();

        // Backend returns the raw array; also tolerate `{ data: ... }`.
        let settings: any = null;
        if (Array.isArray(response)) {
          settings = response[0];
        } else if (response?.data) {
          settings = Array.isArray(response.data) ? response.data[0] : response.data;
        } else {
          settings = response;
        }

        if (!settings) return; // no company record yet → keep initial defaults

        const truthy = (v: any) => v === true || v === 1 || v === "true";

        // Company values for the 5 per-employee flags.
        const companyPf = truthy(settings.pfApplicable);
        const companyEsic = truthy(settings.esicApplicable);
        const companyPt = truthy(settings.ptApplicable);
        const companyLwf = truthy(settings.lwfApplicable);
        const companyTds = truthy(settings.tdsApplicable);
        const tax = employee?.TaxConfig?.[0];
        let pfApp = companyPf;
        let esicApp = companyEsic;
        let ptApp = companyPt;
        let lwfApp = companyLwf;
        let tdsApp = companyTds;

        if (tax) {
          pfApp = truthy(tax.pfApplicable);
          esicApp = truthy(tax.esicApplicable);
          ptApp = truthy(tax.ptApplicable);
          lwfApp = truthy(tax.lwfApplicable);
          tdsApp = truthy(tax.tdsApplicable);
        }

        setPfApplicable(pfApp);
        setEsicApplicable(esicApp);
        setPtApplicable(ptApp);
        setLwfApplicable(lwfApp);

        // When the employee already has their own saved tax config, that
        // per-employee record (regime, PF basis/rate, declarations) wins
        // over the company defaults — otherwise reopening the edit panel
        // would silently reset a saved "Old Regime" (or a custom PF rate /
        // declaration) back to the company's default. Fall back to company
        // settings only for fields never saved for this employee.
        const pickTax = (field: string, fallback: any) => {
          const v = tax ? (tax as any)[field] : undefined;
          return v === undefined || v === null || v === "" ? fallback : v;
        };

        setFormData(prev => ({
          ...prev,
          tdsApplicable: tdsApp,
          pfBasis: pickTax("pfBasis", settings.pfBasis || "statutory"),
          taxRegime: pickTax("taxRegime", settings.taxRegime || "new"),
        }));

        setPfBasis(pickTax("pfBasis", settings.pfBasis || "statutory"));
        setPtState(pickTax("ptState", settings.ptState || "karnataka"));
        setLwfState(pickTax("lwfState", settings.lwfState || "karnataka"));
        setPfFromCtc(truthy(settings.pfFromCtc));
        setPfEmployeeRate(
          Math.max(
            12,
            Number(pickTax("pfEmployeeRate", (employee as any)?.pfEmployeeRate || settings.pfEmployeeRate || 12)),
          ),
        );

        setDeclaration(prev => ({
          ...prev,
          sec80c: Number(pickTax("sec80c", settings.sec80c)) || 0,
          sec80d: Number(pickTax("sec80d", settings.sec80d)) || 0,
          sec80ccd1b: Number(pickTax("sec80ccd1b", settings.sec80ccd1b)) || 0,
          hraExempt: Number(pickTax("hraExempt", settings.hraExempt)) || 0,
          homeLoan: Number(pickTax("homeLoan", settings.homeLoan)) || 0,
          ltaExempt: Number(pickTax("ltaExempt", settings.ltaExempt)) || 0,
          sec80g: Number(pickTax("sec80g", settings.sec80g)) || 0,
          npsEmployer: Number(pickTax("npsEmployer", settings.npsEmployer)) || 0,
          ptPaid: Number(pickTax("ptPaid", settings.ptPaid)) || 0,
          otherDed: Number(pickTax("otherDed", settings.otherDed)) || 0,
        }));
      } catch (error) {
        console.error("Error fetching compliance settings:", error);
      }
    };

    fetchComplianceSettings();
  }, [employee?.id, employee?.TaxConfig?.length]);

  const showReportingPerson = true;
  const managerList = useSelector((state: any) => state.employee.managerList);
  const departmentList = useSelector((state: any) => state.employee.departmentList);
  const designationList = useSelector((state: any) => state.employee.designationList);

  const selectedDepartment = departmentList.find((d: any) => d.id === formData.department_id);
  const isExecutiveDepartment =
    (selectedDepartment?.name || "").trim().toLowerCase() === "executive";
  const ROLES_HIDDEN_FOR_EXECUTIVE = ["employee", "supervisor", "manager"];
  const visibleRoles = isExecutiveDepartment
    ? allowedRoles.filter((r) => !ROLES_HIDDEN_FOR_EXECUTIVE.includes(r))
    : allowedRoles;

  useEffect(() => {
    if (isExecutiveDepartment && ROLES_HIDDEN_FOR_EXECUTIVE.includes(formData.role)) {
      setFormData((prev) => ({ ...prev, role: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExecutiveDepartment]);

  const adminFromList = managerList.find((u: any) => u.id === "admin");
  const ADMIN_OPTION = adminFromList || { id: "admin", name: "Admin", role: "admin" };

  const executiveDepartmentIds = new Set(
    (departmentList || [])
      .filter((d: any) => (d?.name || "").trim().toLowerCase() === "executive")
      .map((d: any) => d.id)
  );

  const filteredManagerList = managerList
    .filter((user: any) => user.id !== "admin")
    .filter((user: any) => {
      const isExecutive =
        !!user.department_id && executiveDepartmentIds.has(user.department_id);

      // No department picked yet → restrict the default list to Admin
      // (added separately below) + Executive-department employees only.
      // Otherwise we'd surface managers / supervisors that the user
      // hasn't chosen a reporting line for yet.
      if (!formData.department_id) return isExecutive;

      // Executive employees are always eligible once a department is set.
      if (isExecutive) return true;

      // Department-scoped role hierarchy for everyone else.
      if (formData.role === "manager") return user.role === "hr head";
      if (formData.role === "supervisor") return ["hr head", "manager"].includes(user.role);
      if (formData.role === "employee") return ["hr head", "manager", "supervisor"].includes(user.role);
      if (formData.role === "director") return ["hr head", "manager", "supervisor"].includes(user.role);
      if (formData.role === "founder") return ["hr head", "manager", "supervisor"].includes(user.role);
      return false;
    });

  const reportingOptions = [ADMIN_OPTION, ...filteredManagerList];

  // Auto-select Admin as the default reporting person when no manager is set yet.
  useEffect(() => {
    if (showReportingPerson && !formData.manager_id) {
      setFormData((prev) => ({ ...prev, manager_id: ADMIN_OPTION.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReportingPerson]);

  const handleAddDepartment = async () => {
    const name = (departmentName || "").trim();
    if (!name) {
      toast({
        title: "Name required",
        description: "Please enter a department name before saving.",
        variant: "destructive",
      });
      return;
    }
    // Client-side duplicate check (case-insensitive). Backend also rejects
    // duplicates with a 400 — caught below in case the list is stale.
    const existing = (departmentList || []).find(
      (d: any) => (d.name || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      toast({
        title: "Department already exists",
        description: `“${existing.name}” is already in the list — pick it from the dropdown instead.`,
        variant: "destructive",
      });
      return;
    }
    try {
      await api.post("/add_department", { name });
      setDepartmentName("");
      setAddDepartmentModal(false);
      dispatch(getDepartmentList());
      toast({
        title: "Department added",
        description: `“${name}” is now available in the dropdown.`,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Could not add department";
      const isDuplicate =
        err?.response?.status === 400 &&
        typeof detail === "string" &&
        detail.toLowerCase().includes("already");
      toast({
        title: isDuplicate ? "Department already exists" : "Could not add department",
        description: detail,
        variant: "destructive",
      });
    }
  };

  const handleAddDesignation = async () => {
    const name = (designationName || "").trim();
    if (!name) {
      toast({
        title: "Name required",
        description: "Please enter a designation name before saving.",
        variant: "destructive",
      });
      return;
    }
    const existing = (designationList || []).find(
      (d: any) => (d.name || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      toast({
        title: "Designation already exists",
        description: `“${existing.name}” is already in the list — pick it from the dropdown instead.`,
        variant: "destructive",
      });
      return;
    }
    try {
      await api.post("/add_designation", { name });
      setDesignationName("");
      setAddDesignationModal(false);
      dispatch(getDesignationList());
      toast({
        title: "Designation added",
        description: `“${name}” is now available in the dropdown.`,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || "Could not add designation";
      const isDuplicate =
        err?.response?.status === 400 &&
        typeof detail === "string" &&
        detail.toLowerCase().includes("already");
      toast({
        title: isDuplicate ? "Designation already exists" : "Could not add designation",
        description: detail,
        variant: "destructive",
      });
    }
  };

  const { toast } = useToast();

  const REQUIRED_PERSONAL_FIELDS = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'employeeID', label: 'Employee ID' },
    { key: 'email', label: 'Email Address' },
    { key: 'gender', label: 'Gender' },
    { key: 'phone', label: 'Phone Number' },
    { key: 'department_id', label: 'Department' },
    { key: 'role', label: 'Role' },
    { key: 'designation_id', label: 'Designation' },
    { key: 'hire_date', label: 'Date of Joining' },
    { key: 'country', label: 'Country' },
    { key: 'current_address1', label: 'Current Address Line 1' },
    { key: 'current_city', label: 'Current City' },
    { key: 'current_state', label: 'Current State' },
    { key: 'current_pin', label: 'Current Pincode' },
  ];

  const ALWAYS_REQUIRED_KEYS = new Set([
    'email',
    'employeeID',
    'hire_date',
    ...(employee?.id ? [] : ['password']),
  ]);

  const isReq = (key: string): boolean => {
    if (ALWAYS_REQUIRED_KEYS.has(key)) return true;
    if (isMandatoryDisabled) return false;
    return REQUIRED_PERSONAL_FIELDS.some((f) => f.key === key);
  };

  // Tiny red asterisk used after every required label.
  const Req = ({ on }: { on: boolean }) =>
    on ? <span className="text-red-500 ml-0.5">*</span> : null;

  const formRef = useRef<HTMLFormElement>(null);

  const getRequiredFields = () => {
    const fields = [...REQUIRED_PERSONAL_FIELDS];
    if (!employee?.id) {
      fields.push({ key: 'password', label: 'Password' });
    }
    return fields;
  };

  const validatePersonalDetails = (): string[] => {

    // ✅ ADD THIS LINE (FULL SKIP)
    if (isMandatoryDisabled) {
      return [];
    }

    const missingFields: string[] = [];
    const requiredFields = getRequiredFields();

    for (const field of requiredFields) {
      const value = formData[field.key as keyof typeof formData];

      if (!value || (typeof value === 'string' && value.trim() === '')) {
        missingFields.push(field.label);
      }
    }

    // optional validations (only if value exists)
    if (formData.phone && formData.phone.length !== 10) {
      missingFields.push('Phone Number (must be 10 digits)');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      missingFields.push('Valid Email Address');
    }

    if (formData.current_pin && !/^\d{6}$/.test(formData.current_pin)) {
      missingFields.push('Current Pincode (must be 6 digits)');
    }

    return missingFields;
  };

  const scrollToTopOfPage = () => {
    const doScroll = () => {
      try {
        let el: HTMLElement | null = formRef.current;
        while (el) {
          const style = window.getComputedStyle(el);
          const oy = style.overflowY;
          const scrollable =
            (oy === "auto" || oy === "scroll" || oy === "overlay") &&
            el.scrollHeight > el.clientHeight;
          if (scrollable) el.scrollTop = 0;
          el = el.parentElement;
        }
      } catch {}
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
      if (typeof document !== "undefined") {
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      }
    };

    doScroll();
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(doScroll);
    }
  };

  const handleNext = () => {
    if (activeTab === "personal") {
      const form = formRef.current;
      if (form && !form.checkValidity()) {
        form.reportValidity();
        return;
      }
      setActiveTab("salary");
    } else if (activeTab === "salary") {
      setActiveTab("documents");
    } else if (activeTab === "documents") {
      setActiveTab("summary");
    }
    scrollToTopOfPage();
  };
  const handleBack = () => {
    if (activeTab === "salary") {
      setActiveTab("personal");
    } else if (activeTab === "documents") {
      setActiveTab("salary");
    } else if (activeTab === "summary") {
      setActiveTab("documents");
    }
    scrollToTopOfPage();
  };

 const buildTaxConfig = () => {
  return [
    {
      pfApplicable: pfApplicable,
      esicApplicable: esicApplicable,
      ptApplicable: ptApplicable,
      lwfApplicable: lwfApplicable,
      tdsApplicable: formData.tdsApplicable,
      // Per-employee tax policy — must be sent so the regime, PF basis/rate
      // and TDS declarations are saved against this employee (and not lost
      // back to the company defaults on the next edit).
      pfBasis: pfBasis,
      pfEmployeeRate: pfEmployeeRate,
      ptState: ptState,
      lwfState: lwfState,
      taxRegime: formData.taxRegime,
      sec80c: declaration.sec80c,
      sec80d: declaration.sec80d,
      sec80ccd1b: declaration.sec80ccd1b,
      hraExempt: declaration.hraExempt,
      homeLoan: declaration.homeLoan,
      ltaExempt: declaration.ltaExempt,
      sec80g: declaration.sec80g,
      npsEmployer: declaration.npsEmployer,
      ptPaid: declaration.ptPaid,
      otherDed: declaration.otherDed,
    },
  ];
};

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const salaryDetailsArray = salaryRecords.map(record => {
        const details = calculateSalaryDetailsForRecord(record);
        return {
          basicsalary: record.basicSalary,
          hra: record.hra,
          conveyance: record.conveyance,
          medicalallowance: record.medicalallowance,
          allowance: record.allowance,
          lta: record.lta,
          others: record.others,
          bonus: record.bonusAmount,
          pf: details.pfEE,
          esi: details.esicEE,
          proftax: details.pt,
          labourwelfare: details.lwfEE,
          tds: details.tds,
          netsalary: details.net,
          effectiveDate: record.effectiveDate,
          status: record.status,
          customComponents: record.customComponents
        };
      });

      const finalData = {
        ...formData,
        designation: getDesignationName(formData.designation_id),
        SalaryDetails: salaryDetailsArray,
        TaxConfig: buildTaxConfig(),
        basicsalary: salaryRecords[0]?.basicSalary || 0,
        hra: salaryRecords[0]?.hra || 0,
        conveyance: salaryRecords[0]?.conveyance || 0,
        medicalallowance: salaryRecords[0]?.medicalallowance || 0,
        allowance: salaryRecords[0]?.allowance || 0,
        lta: salaryRecords[0]?.lta || 0,
        others: salaryRecords[0]?.others || 0,
        bonus: salaryRecords[0]?.bonusAmount || 0,
        netsalary: calculateSalaryDetailsForRecord(salaryRecords[0]).net,
        salary_amount: calculateSalaryDetailsForRecord(salaryRecords[0]).gross,
        pfApplicable,
        esicApplicable,
        ptApplicable,
        lwfApplicable,
        pfBasis,
        pfEmployeeRate,
        ptState,
        lwfState,
        tds: calculateSalaryDetailsForRecord(salaryRecords[0]).tds,
        ...declaration,
      };

      const result: any = await onSubmit(finalData);

      const newId =
        result?.id ||
        result?.employee?.id ||
        result?.data?.id ||
        finalData.id;
      const stagedEntries = Object.entries(pendingDocs) as [EmployeeDocType, File][];
      if (newId && stagedEntries.length > 0) {
        for (const [docType, file] of stagedEntries) {
          try {
            await uploadEmployeeDocument(newId, docType, file);
          } catch (err: any) {
            toast({
              title: `Failed to upload ${docType.replace("_", " ")}`,
              description: err?.response?.data?.detail || err?.message || "Upload error",
              variant: "destructive",
            });
          }
        }
        setPendingDocs({});
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDepartmentName = (deptId: string) => {
    const dept = departmentList.find((d: any) => d.id === deptId);
    return dept?.name || deptId;
  };

  const getDesignationName = (designationId: string) => {
    const designation = designationList.find((d: any) => d.id === designationId);
    return designation?.name || designationId;
  };

  // The state mock labels are stored with a leading GST state code like
  // "17 Meghalaya". Strip the prefix when rendering / filtering so users see
  // just the state name and search matches against the name only.
  const cleanStateLabel = (label: string) => (label || "").replace(/^\d+\s*/, "");

  const getStateName = (stateId: string) => {
    const stateObj = state.find((s) => String(s.id) === stateId);
    return cleanStateLabel(stateObj?.label || stateId);
  };

  const getCountryName = (countryId: string) => {
    const countryObj = country.find((c) => String(c.id) === countryId);
    return countryObj?.label || countryId;
  };

  const getManagerName = (managerId: string) => {
    if (managerId === "admin") {
      const admin = managerList.find((m: any) => m.id === "admin");
      return admin?.name || "Admin";
    }
    const manager = managerList.find((m: any) => m.id === managerId);
    return manager?.name || managerId;
  };

  const isNewRegime = formData.taxRegime === "new";

  useEffect(() => {
    if (employee && designationList.length > 0) {
      const matchedDesignation = designationList.find(
        (d: any) => d.name === employee.designation
      );

      if (matchedDesignation) {
        setFormData((prev) => ({
          ...prev,
          designation_id: matchedDesignation.id,
        }));
      }
    }
  }, [employee, designationList]);

  const isAddMode = !employee;
  const tabsLocked = isAddMode;

  const TAB_ORDER: EmployeeStep[] = ["personal", "salary", "documents", "summary"];
  const tabIndex = (t: EmployeeStep) => TAB_ORDER.indexOf(t);
  const [maxReachedTab, setMaxReachedTab] = useState<EmployeeStep>(
    initialTab || "personal",
  );
  useEffect(() => {
    setMaxReachedTab((prev) =>
      tabIndex(activeTab) > tabIndex(prev) ? activeTab : prev,
    );
  }, [activeTab]);

  const personalMissingFields = isAddMode ? validatePersonalDetails() : [];
  const personalIsValid = personalMissingFields.length === 0;

  const unlockedTabs: EmployeeStep[] = (() => {
    if (!isAddMode) return TAB_ORDER;
    if (personalIsValid) return TAB_ORDER;
    const reachedIdx = tabIndex(maxReachedTab);
    return TAB_ORDER.slice(0, reachedIdx + 1);
  })();

  const unlockedTabsKey = unlockedTabs.join(",");
  useEffect(() => {
    onUnlockedTabsChange?.(unlockedTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockedTabsKey, onUnlockedTabsChange]);

  // ─── Inline email / phone validation helpers ──────────────────────
  const normalize = (s: string | undefined | null) =>
    (s || "").trim().toLowerCase();
  // Same regex used by validatePersonalDetails, kept in sync so the
  // submit-time check and the inline indicator never disagree.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailFormatInvalid =
    !!formData.email && !EMAIL_RE.test(formData.email.trim());
  const isEmailDuplicate =
    !!formData.email &&
    !isEmailFormatInvalid &&
    (existingEmails || []).some(
      (e) => normalize(e) === normalize(formData.email),
    );
  const isPhoneDuplicate =
    !!formData.phone &&
    (existingPhones || []).some(
      (p) => (p || "").replace(/\D/g, "") === (formData.phone || "").replace(/\D/g, ""),
    );

  return (
    <form
      ref={formRef}
      id="employee-form"
      onSubmit={handleFinalSubmit}
      className="space-y-6"
    >
      {!hideTabNav && (
        <EmployeeStepsTabNav
          activeTab={activeTab}
          tabsLocked={tabsLocked}
          unlockedTabs={isAddMode ? unlockedTabs : undefined}
          onTabChange={(next) => {
            setActiveTab(next);
            scrollToTopOfPage();
          }}
        />
      )}

      {/* Personal Details Tab */}
      {activeTab === "personal" && (
        <div className="space-y-6">
          {/* Personal Information Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Personal Information
              </CardTitle>
              <CardDescription>Basic personal and contact details</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    First Name<Req on={isReq("first_name")} />
                  </Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        first_name: capitalizeFirstLetter(e.target.value),
                      })
                    }
                    required={isReq("first_name")}
                    className="mt-1.5 font-medium"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Middle Name</Label>
                  <Input
                    value={formData.middle_name}
                    onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                    className="mt-1.5"
                    placeholder="Enter middle name"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Last Name<Req on={isReq("last_name")} />
                  </Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        last_name: capitalizeFirstLetter(e.target.value),
                      })
                    }
                    required={isReq("last_name")}
                    className="mt-1.5 font-medium"
                    placeholder="Enter last name"
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Gender<Req on={isReq("gender")} />
                  </Label>

                  <Select
                    value={formData.gender || ""}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        gender: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Gender" />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="others">Others</SelectItem>

                    </SelectContent>
                  </Select>
                  {/* Hidden field so HTML5 validation also catches Gender if required */}
                  {isReq("gender") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.gender || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Employee ID<Req on={isReq("employeeID")} />
                  </Label>
                  <Input
                    value={formData.employeeID}
                    onChange={(e) => setFormData({ ...formData, employeeID: e.target.value })}
                    required={isReq("employeeID")}
                    className="mt-1.5 font-mono"
                    placeholder="EMP001"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Mail className="h-3 w-3 inline mr-1" />
                    Email Address<Req on={isReq("email")} />
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required={isReq("email")}
                    aria-invalid={isEmailFormatInvalid || isEmailDuplicate || undefined}
                    className={`mt-1.5 ${
                      isEmailFormatInvalid || isEmailDuplicate
                        ? "border-red-400 focus:ring-red-500 focus:border-red-500"
                        : ""
                    }`}
                    placeholder="employee@company.com"
                  />
                  {/* Format error takes precedence — when the address
                      itself is malformed, the duplicate check is moot. */}
                  {isEmailFormatInvalid && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      Enter a valid email address (e.g. name@company.com)
                    </p>
                  )}
                  {isEmailDuplicate && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      This email is already added for another employee
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Phone className="h-3 w-3 inline mr-1" />
                    Phone Number<Req on={isReq("phone")} />
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    minLength={10}
                    pattern="\d{10}"
                    title="Phone number must be exactly 10 digits"
                    placeholder="Enter 10 digit phone number"
                    value={formData.phone}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 10) {
                        setFormData({ ...formData, phone: value });
                      }
                    }}
                    onInvalid={(e) => {
                      // Keep the native tooltip text consistent with our inline message.
                      (e.currentTarget as HTMLInputElement).setCustomValidity(
                        formData.phone
                          ? 'Phone number must be exactly 10 digits'
                          : 'Phone number is required',
                      );
                    }}
                    onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity('')}
                    required={isReq("phone")}
                    aria-invalid={
                      (!!formData.phone && formData.phone.length !== 10) || isPhoneDuplicate
                    }
                    className={`mt-1.5 ${
                      (formData.phone && formData.phone.length !== 10) || isPhoneDuplicate
                        ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                        : ''
                    }`}
                  />
                  {formData.phone && formData.phone.length !== 10 && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      Phone number must be exactly 10 digits ({formData.phone.length}/10)
                    </p>
                  )}
                  {formData.phone && formData.phone.length === 10 && isPhoneDuplicate && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      This phone number is already added for another employee
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Building2 className="h-3 w-3 inline mr-1" />
                    Department<Req on={isReq("department_id")} />
                  </Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <Select
                        value={formData.department_id}
                        onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                        onOpenChange={(open) => { if (!open) setDeptSearch(""); }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {/* Search bar — pinned at the top, stop key events so Radix
                              Select doesn't intercept typing as a typeahead. */}
                          <div
                            className="sticky top-0 z-10 bg-white p-2 border-b"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="relative">
                              <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                autoFocus
                                placeholder="Search department…"
                                value={deptSearch}
                                onChange={(e) => setDeptSearch(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                              />
                            </div>
                          </div>
                          {/* List capped at ~5 rows (≈ 200px) before scroll */}
                          <div className="max-h-[200px] overflow-y-auto">
                            {(() => {
                              const q = deptSearch.trim().toLowerCase();
                              const filtered = q
                                ? departmentList.filter((d: any) => (d.name || "").toLowerCase().includes(q))
                                : departmentList;
                              if (filtered.length === 0) {
                                return (
                                  <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
                                );
                              }
                              return filtered.map((dept: any) => (
                                <SelectItem key={dept.id} value={dept.id}>
                                  {dept.name}
                                </SelectItem>
                              ));
                            })()}
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0"
                      onClick={() => setAddDepartmentModal(true)}
                      aria-label="Add Department"
                      title="Add Department"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {isReq("department_id") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.department_id || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <User className="h-3 w-3 inline mr-1" />
                    Role<Req on={isReq("role")} />
                  </Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {visibleRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          <span className="capitalize">{role.toUpperCase()}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isReq("role") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.role || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <FileText className="h-3 w-3 inline mr-1" />
                    Designation<Req on={isReq("designation_id")} />
                  </Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <Select
                        value={formData.designation_id}
                        onValueChange={(value) =>
                          setFormData({ ...formData, designation_id: value })
                        }
                        onOpenChange={(open) => { if (!open) setDesigSearch(""); }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Designation" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {/* Search bar pinned at top */}
                          <div
                            className="sticky top-0 z-10 bg-white p-2 border-b"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="relative">
                              <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                autoFocus
                                placeholder="Search designation…"
                                value={desigSearch}
                                onChange={(e) => setDesigSearch(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                              />
                            </div>
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {(() => {
                              const q = desigSearch.trim().toLowerCase();
                              const filtered = q
                                ? designationList.filter((d: any) => (d.name || "").toLowerCase().includes(q))
                                : designationList;
                              if (filtered.length === 0) {
                                return (
                                  <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
                                );
                              }
                              return filtered.map((d: any) => (
                                <SelectItem key={d.id} value={d.id}>
                                  {d.name}
                                </SelectItem>
                              ));
                            })()}
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 flex-shrink-0"
                      onClick={() => setAddDesignationModal(true)}
                      aria-label="Add Designation"
                      title="Add Designation"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {isReq("designation_id") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.designation_id || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Date of Joining<Req on={isReq("hire_date")} />
                  </Label>
                  <Input
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                    required={isReq("hire_date")}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Date of Birth
                  </Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth || ""}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Building2 className="h-3 w-3 inline mr-1" />
                    Country<Req on={isReq("country")} />
                  </Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => setFormData({ ...formData, country: value })}
                    onOpenChange={(open) => { if (!open) setCountrySearch(""); }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select Country" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <div
                        className="sticky top-0 z-10 bg-white p-2 border-b"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="relative">
                          <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            autoFocus
                            placeholder="Search country…"
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        </div>
                      </div>
                      {/* List capped at ~5 rows (≈ 200px) before scroll */}
                      <div className="max-h-[200px] overflow-y-auto">
                        {(() => {
                          const q = countrySearch.trim().toLowerCase();
                          const filtered = q
                            ? country.filter((e: any) => String(e.label || "").toLowerCase().includes(q))
                            : country;
                          if (filtered.length === 0) {
                            return <div className="px-3 py-2 text-xs text-gray-500">No matches</div>;
                          }
                          return filtered.map((e: any) => (
                            <SelectItem key={e.id} value={String(e.id)}>
                              {e.label}
                            </SelectItem>
                          ));
                        })()}
                      </div>
                    </SelectContent>
                  </Select>
                  {isReq("country") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.country || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Phone className="h-3 w-3 inline mr-1" />
                    Emergency Contact
                  </Label>
                  <Input
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.emergency_contacts}
                    onChange={(e) => setFormData({ ...formData, emergency_contacts: e.target.value })}
                    className="mt-1.5"
                    placeholder="Emergency contact number"
                  />
                </div>
                <div className="relative">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Password<Req on={isReq("password")} />
                  </Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={isReq("password")}
                    className="mt-1.5 pr-10"
                    placeholder={
                      employee?.id ? "Leave blank to keep same password" : "••••••••"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[38px] text-gray-500"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {showReportingPerson && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <User className="h-3 w-3 inline mr-1" />
                    Reporting Person
                  </Label>
                  <Select
                    value={formData.manager_id}
                    onValueChange={(value) => setFormData({ ...formData, manager_id: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select Reporting Person" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {reportingOptions.map((e: any) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} <span className="text-xs text-gray-500">({e.role.toUpperCase()})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Address Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                Current Address
              </CardTitle>
              <CardDescription>Residential address details</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="lg:col-span-2">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Address Line 1<Req on={isReq("current_address1")} />
                  </Label>
                  <Input
                    value={formData.current_address1}
                    onChange={(e) => setFormData({ ...formData, current_address1: e.target.value })}
                    required={isReq("current_address1")}
                    className="mt-1.5"
                    placeholder="House number, building name"
                  />
                </div>
                <div className="lg:col-span-2">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 2</Label>
                  <Input
                    value={formData.current_address2}
                    onChange={(e) => setFormData({ ...formData, current_address2: e.target.value })}
                    className="mt-1.5"
                    placeholder="Street, area, landmark"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    City<Req on={isReq("current_city")} />
                  </Label>
                  <Input
                    value={formData.current_city}
                    onChange={(e) => setFormData({ ...formData, current_city: e.target.value })}
                    required={isReq("current_city")}
                    className="mt-1.5"
                    placeholder="Enter city"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    State<Req on={isReq("current_state")} />
                  </Label>
                  <Select
                    value={formData.current_state}
                    onValueChange={(value) => setFormData({ ...formData, current_state: value })}
                    onOpenChange={(open) => { if (!open) setCurrStateSearch(""); }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <div
                        className="sticky top-0 z-10 bg-white p-2 border-b"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="relative">
                          <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            autoFocus
                            placeholder="Search state…"
                            value={currStateSearch}
                            onChange={(e) => setCurrStateSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        </div>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto">
                        {(() => {
                          const q = currStateSearch.trim().toLowerCase();
                          const filtered = q
                            ? state.filter((e: any) => cleanStateLabel(e.label).toLowerCase().includes(q))
                            : state;
                          if (filtered.length === 0) {
                            return <div className="px-3 py-2 text-xs text-gray-500">No matches</div>;
                          }
                          return filtered.map((e: any) => (
                            <SelectItem key={e.id} value={String(e.id)}>
                              {cleanStateLabel(e.label)}
                            </SelectItem>
                          ));
                        })()}
                      </div>
                    </SelectContent>
                  </Select>
                  {isReq("current_state") && (
                    <input
                      tabIndex={-1}
                      aria-hidden="true"
                      required
                      value={formData.current_state || ""}
                      onChange={() => { }}
                      style={{ opacity: 0, height: 0, padding: 0, border: 0, position: "absolute" }}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Pincode<Req on={isReq("current_pin")} />
                  </Label>
                  <Input
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.current_pin}
                    onChange={(e) => setFormData({ ...formData, current_pin: e.target.value })}
                    required={isReq("current_pin")}
                    className="mt-1.5"
                    placeholder="6 digit code"
                    pattern="\d{6}"
                    title="6 digit code"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Mode of Working</Label>
                  <Select
                    value={formData.modeOfWorking}
                    onValueChange={async (value) => {
                      // Gate "in-office" on a configured geofence — the
                      // attendance flow needs office lat / lon / radius
                      // to decide whether the employee is on premises.
                      // Remote / hybrid don't need this.
                      if (value === "in-office") {
                        const ok = await officeHasGeofence();
                        if (!ok) {
                          setOfficeLocationMissingOpen(true);
                          return; // don't apply the change
                        }
                      }
                      setFormData({
                        ...formData,
                        modeOfWorking: value,
                        workfromhome: value === "remote" || value === "hybrid",
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="in-office">🏢 In Office</SelectItem>
                      <SelectItem value="remote">🏠 Remote</SelectItem>
                      <SelectItem value="hybrid">🔄 Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Permanent Address Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  <CardTitle>Permanent Address</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sameAs"
                    checked={formData.sameAs}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({
                          ...formData,
                          sameAs: true,
                          permanent_address1: formData.current_address1,
                          permanent_address2: formData.current_address2,
                          permanent_city: formData.current_city,
                          permanent_state: formData.current_state,
                          permanent_pin: formData.current_pin,
                        });
                      } else {
                        setFormData({
                          ...formData,
                          sameAs: false,
                          permanent_address1: "",
                          permanent_address2: "",
                          permanent_city: "",
                          permanent_state: "",
                          permanent_pin: "",
                        });
                      }
                    }}
                  />
                  <Label htmlFor="sameAs" className="text-sm cursor-pointer">
                    Same as current address
                  </Label>
                </div>
              </div>
              <CardDescription>Permanent or correspondence address</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {formData.sameAs === false && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 1</Label>
                    <Input
                      value={formData.permanent_address1}
                      onChange={(e) => setFormData({ ...formData, permanent_address1: e.target.value })}
                      className="mt-1.5"
                      placeholder="House number, building name"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 2</Label>
                    <Input
                      value={formData.permanent_address2}
                      onChange={(e) => setFormData({ ...formData, permanent_address2: e.target.value })}
                      className="mt-1.5"
                      placeholder="Street, area, landmark"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">City</Label>
                    <Input
                      value={formData.permanent_city}
                      onChange={(e) => setFormData({ ...formData, permanent_city: e.target.value })}
                      className="mt-1.5"
                      placeholder="Enter city"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">State</Label>
                    <Select
                      value={formData.permanent_state}
                      onValueChange={(value) => setFormData({ ...formData, permanent_state: value })}
                      onOpenChange={(open) => { if (!open) setPermStateSearch(""); }}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <div
                          className="sticky top-0 z-10 bg-white p-2 border-b"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="relative">
                            <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              autoFocus
                              placeholder="Search state…"
                              value={permStateSearch}
                              onChange={(e) => setPermStateSearch(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()}
                              className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto">
                          {(() => {
                            const q = permStateSearch.trim().toLowerCase();
                            const filtered = q
                              ? state.filter((e: any) => cleanStateLabel(e.label).toLowerCase().includes(q))
                              : state;
                            if (filtered.length === 0) {
                              return <div className="px-3 py-2 text-xs text-gray-500">No matches</div>;
                            }
                            return filtered.map((e: any) => (
                              <SelectItem key={e.id} value={String(e.id)}>
                                {cleanStateLabel(e.label)}
                              </SelectItem>
                            ));
                          })()}
                        </div>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pincode</Label>
                    <Input
                      value={formData.permanent_pin}
                      onChange={(e) => setFormData({ ...formData, permanent_pin: e.target.value })}
                      className="mt-1.5"
                      placeholder="6 digit code"
                    />
                  </div>
                </div>
              )}
              {formData.sameAs && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <MapPin className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>Permanent address is same as current address</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Remote / hybrid employees mark attendance from anywhere — no
              label, schedule, location, or geofence is collected on the
              form. workfromhome_list stays empty; the backend schema
              already accepts an empty list, so the existing payload shape
              and downstream consumers continue to work. */}

          <div className="flex justify-end pt-4">
            <Button
              type="button"
              onClick={handleNext}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              Next: Salary & Deductions
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Salary & Deductions Tab with Multiple Records */}
      {activeTab === "salary" && (
        <div className="space-y-6">
          {/* Compact salary setup — only CTC, effective date, and tax
              regime are required at the time of add/update. Components
              are auto-calculated from CTC; the "View / Edit Salary
              Components" button below reveals the per-record detail
              cards for admins who need to tweak the computed split. */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-5 rounded-lg border border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  CTC <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    placeholder="Enter CTC"
                    value={ctcValue || ''}
                    onChange={(e) => {
                      // User-driven CTC edit → allow the auto-split to run.
                      skipCtcAutoSplitRef.current = false;
                      setCtcValue(Number(e.target.value));
                    }}
                    className="flex-1"
                  />
                  <Select
                    value={isYearly ? "yearly" : "monthly"}
                    onValueChange={(val) => {
                      skipCtcAutoSplitRef.current = false;
                      setIsYearly(val === "yearly");
                    }}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yearly">Yearly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {salaryRecords[0] && ctcValue > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Monthly CTC: ₹{fmt(calculateSalaryDetailsForRecord(salaryRecords[0]).ctc)} ·
                    Net pay: ₹{fmt(calculateSalaryDetailsForRecord(salaryRecords[0]).net)}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Effective Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="date"
                  value={salaryRecords[0]?.effectiveDate || ''}
                  onChange={(e) =>
                    salaryRecords[0] && updateSalaryRecord(salaryRecords[0].id, 'effectiveDate', e.target.value)
                  }
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Tax Regime <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.taxRegime}
                  onValueChange={(value) => setFormData({ ...formData, taxRegime: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Regime" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New Regime (Default)</SelectItem>
                    <SelectItem value="old">Old Regime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowSalaryDetails(prev => !prev)}
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                {showSalaryDetails ? 'Hide' : 'View / Edit'} Salary Components
                <span className="ml-1">{showSalaryDetails ? '▲' : '▼'}</span>
              </Button>
            </div>
          </div>

          {/* Detailed per-record component cards — only rendered when
              the admin opens "View / Edit Salary Components". The
              auto-calculated split still drives the saved payload even
              when this section is collapsed. */}
          {showSalaryDetails && salaryRecords.map((record, index) => {
            const salaryDetails = calculateSalaryDetailsForRecord(record);
            const isCurrentRecord = index === 0;

            return (
              <Card key={record.id} className={`border-0 shadow-md relative ${!isCurrentRecord ? 'opacity-95' : ''}`}>
                {/* Record Header with Date and Status */}
                <div className={`px-6 py-3 rounded-t-xl flex justify-between items-center ${isCurrentRecord ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-blue-50 to-indigo-50'} border-b`}>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${record.status === 'active' ? 'bg-green-500' : record.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-500'}`}></div>
                      <span className="text-sm font-semibold text-gray-700">
                        Salary Component {index + 1}
                        {isCurrentRecord && <span className="ml-2 text-xs text-green-600">(Current)</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-500">Effective Date:</Label>
                      <Input
                        type="date"
                        value={record.effectiveDate}
                        onChange={(e) => updateSalaryRecord(record.id, 'effectiveDate', e.target.value)}
                        className="h-8 w-40 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-500">Status:</Label>
                      <Select
                        value={record.status}
                        onValueChange={(value) => updateSalaryRecord(record.id, 'status', value)}
                      >
                        <SelectTrigger className="h-8 w-32 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {salaryRecords.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSalaryRecord(record.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <CardContent className="p-6">
                  {/* Add Custom Component Button for each record */}
                  <div className="flex justify-end mb-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const updatedRecords = [...salaryRecords];
                        const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                        updatedRecords[recordIndex].customComponents = [
                          ...(updatedRecords[recordIndex].customComponents || []),
                          { id: Date.now().toString(), name: "", amount: 0 }
                        ];
                        setSalaryRecords(updatedRecords);
                      }}
                      className="border-green-300 text-green-600 hover:bg-green-50"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Component
                    </Button>
                  </div>

                  {/* Standard Components Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Basic Salary *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.basicSalary || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'basicSalary', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">HRA (House Rent Allowance)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.hra || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'hra', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Conveyance Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.conveyance || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'conveyance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Medical Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.medicalallowance || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'medicalallowance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Special Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.allowance || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'allowance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">LTA (Leave Travel Allowance)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.lta || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'lta', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Other Allowances</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.others || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'others', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Bonus / Incentive</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.bonusAmount || ""}
                        onChange={(e) => updateSalaryRecord(record.id, 'bonusAmount', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Custom Components Section for this record */}
                  {(record.customComponents && record.customComponents.length > 0) && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <Plus className="h-4 w-4 text-green-600" />
                          Custom Components
                        </Label>
                        <span className="text-xs text-gray-500">Click on component name to edit</span>
                      </div>
                      <div className="space-y-3">
                        {record.customComponents.map((component: any, compIndex: number) => (
                          <div key={component.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs font-semibold text-gray-500">Component Name</Label>
                                <Input
                                  type="text"
                                  value={component.name}
                                  onChange={(e) => {
                                    const updatedRecords = [...salaryRecords];
                                    const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                    updatedRecords[recordIndex].customComponents[compIndex].name = e.target.value;
                                    setSalaryRecords(updatedRecords);
                                  }}
                                  className="mt-1"
                                  placeholder="e.g., Performance Bonus, Night Shift Allowance, etc."
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-semibold text-gray-500">Amount (₹)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={component.amount || ""}
                                  onChange={(e) => {
                                    const updatedRecords = [...salaryRecords];
                                    const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                    updatedRecords[recordIndex].customComponents[compIndex].amount = Number(e.target.value);
                                    setSalaryRecords(updatedRecords);
                                  }}
                                  className="mt-1"
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updatedRecords = [...salaryRecords];
                                const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                updatedRecords[recordIndex].customComponents = updatedRecords[recordIndex].customComponents.filter((_: any, i: number) => i !== compIndex);
                                setSalaryRecords(updatedRecords);
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-5"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gross Monthly Summary */}
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-green-800">Gross Monthly (Full Month)</span>
                      <span className="text-xl font-bold text-green-600">
                        ₹{fmt(salaryDetails.gross)}
                      </span>
                    </div>
                    {(record.customComponents && record.customComponents.length > 0) && (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <div className="text-xs text-green-700">
                          Includes: {record.customComponents.map((c: any) => c.name).filter(n => n).join(", ")}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Statutory Deductions for this record */}
                  <div className="mt-4">
                    <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <div className="text-xs text-gray-500">EPF (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.pfEE)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">ESIC (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.esicEE)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Professional Tax</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.pt)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">LWF (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.lwfEE)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-100">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">TDS (Monthly)</span>
                        <span className="text-sm font-bold text-orange-600">₹{fmt(salaryDetails.tds)}</span>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-blue-800">Net Pay (Take Home)</span>
                        <span className="text-xl font-bold text-blue-600">₹{fmt(salaryDetails.net)}</span>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-purple-800">Monthly CTC</span>
                        <span className="text-sm font-bold text-purple-600">₹{fmt(salaryDetails.ctc)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Add Another Salary Component Button — lives with the
              detailed cards so it only appears in expanded mode. */}
          {showSalaryDetails && (
            <Button
              type="button"
              variant="outline"
              onClick={addSalaryRecord}
              className="w-full border-dashed border-2 py-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Another Salary Component
            </Button>
          )}

          {/* Statutory Settings Card */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-red-50 to-rose-50">
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-red-600" />
                Statutory Settings
              </CardTitle>
              <CardDescription>Configure statutory deductions applicable to this employee</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={pfApplicable === true}
                      onChange={(e) => setPfApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">EPF Applicable (EE 12% + ER 12%)</label>
                  </div>
                  {pfApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={pfBasis}
                        onChange={(e) => setPfBasis(e.target.value as "statutory" | "actual")}
                      >
                        <option value="statutory">Statutory (capped ₹15,000 basic)</option>
                        <option value="actual">Actual Basic Salary</option>
                      </select>
                      <div className="flex items-center gap-1">
                        <label className="text-sm text-gray-600 whitespace-nowrap">
                          Employee PF rate (%)
                        </label>
                        <input
                          type="number"
                          min={12}
                          step={0.5}
                          value={pfEmployeeRate}
                          onChange={(e) => setPfEmployeeRate(Number(e.target.value))}
                          onBlur={(e) => {
                            if (Number(e.target.value) < 12) setPfEmployeeRate(12);
                          }}
                          className="border rounded p-1 text-sm w-20"
                        />
                      </div>
                      <span className="text-xs text-gray-400">
                        Employer fixed at 12%.
                        {pfBasis === "statutory" && " Statutory basis caps the base at ₹15,000."}
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={esicApplicable === true}
                      onChange={(e) => setEsicApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">ESIC Applicable (gross ≤ ₹21,000)</label>
                  </div>
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={ptApplicable === true}
                      onChange={(e) => setPtApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Professional Tax</label>
                  </div>
                  {ptApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={ptState}
                        onChange={(e) => setPtState(e.target.value)}
                      >
                        {Object.entries(PT_STATES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={lwfApplicable === true}
                      onChange={(e) => setLwfApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Labour Welfare Fund (LWF)</label>
                  </div>
                  {lwfApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={lwfState}
                        onChange={(e) => setLwfState(e.target.value)}
                      >
                        {Object.entries(LWF_STATES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={formData.tdsApplicable === true}
                      onChange={(e) => setFormData({ ...formData, tdsApplicable: e.target.checked })}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Deduct TDS (Income Tax)</label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TDS Declaration Section - Only for Old Regime */}
          {formData.tdsApplicable && !isNewRegime && (
            <Card className="border-0 shadow-md">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
                <CardTitle className="text-lg">TDS Declaration (Form 12BB)</CardTitle>
                <CardDescription>Employee tax saving declarations for Old Regime</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80C (PPF/ELSS/LIC/EPF) - Max ₹1,50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80c || ""}
                      onChange={(e) => setDeclaration({ ...declaration, sec80c: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80D (Health Insurance) - Max ₹50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80d || ""}
                      onChange={(e) => setDeclaration({ ...declaration, sec80d: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80CCD(1B) (NPS Self) - Max ₹50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80ccd1b || ""}
                      onChange={(e) => setDeclaration({ ...declaration, sec80ccd1b: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">HRA Exemption (Annual)</Label>
                    <Input
                      type="number"
                      value={declaration.hraExempt || ""}
                      onChange={(e) => setDeclaration({ ...declaration, hraExempt: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Home Loan Interest (Sec 24b) - Max ₹2,00,000</Label>
                    <Input
                      type="number"
                      value={declaration.homeLoan || ""}
                      onChange={(e) => setDeclaration({ ...declaration, homeLoan: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">LTA Exemption (Annual)</Label>
                    <Input
                      type="number"
                      value={declaration.ltaExempt || ""}
                      onChange={(e) => setDeclaration({ ...declaration, ltaExempt: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80G (Donations)</Label>
                    <Input
                      type="number"
                      value={declaration.sec80g || ""}
                      onChange={(e) => setDeclaration({ ...declaration, sec80g: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Employer NPS (Sec 80CCD(2)) - Monthly</Label>
                    <Input
                      type="number"
                      value={declaration.npsEmployer || ""}
                      onChange={(e) => setDeclaration({ ...declaration, npsEmployer: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* New Regime Note */}
          {formData.tdsApplicable && isNewRegime && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="font-semibold text-blue-800 text-sm mb-1">New Regime Benefits</div>
              <div className="text-xs text-blue-700">
                • Standard Deduction: ₹75,000<br />
                • Rebate u/s 87A: Up to ₹60,000 for income ≤ ₹12L (effectively zero tax up to ₹12.75L)<br />
                • Only Employer NPS (80CCD(2)) is deductible<br />
                • No other deductions (80C, 80D, HRA, etc.) are allowed
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back: Personal Details
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              Next: Upload Documents
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Upload Documents Tab — all uploads are OPTIONAL */}
      {activeTab === "documents" && (
        <div className="space-y-6">
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Upload Documents
                <span className="text-xs font-normal text-gray-500 ml-2">(Optional)</span>
              </CardTitle>
              <CardDescription>

                {!employee?.id}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {([
                  { key: "aadhar",            label: "Aadhar Card" },
                  { key: "pan",               label: "PAN Card" },
                  { key: "cv",                label: "CV / Resume" },
                  { key: "relieving_letter",  label: "Relieving Letter" },
                  { key: "form16",            label: "Form 16" },
                  { key: "experience_letter", label: "Experience Letter" },
                ] as { key: EmployeeDocType; label: string }[]).map((doc) => {
                  const state = docsState[doc.key];
                  const uploaded = !!state?.blob_path;
                  const staged = !!pendingDocs[doc.key];
                  const path = state?.public_url || state?.blob_path || "";
                  const ext = path.split("?")[0].split(".").pop()?.toLowerCase() || "";
                  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext);
                  const isPdf = ext === "pdf";
                  return (
                    <DocUploadCard
                      key={doc.key}
                      doc={doc}
                      state={state}
                      staged={staged}
                      uploaded={uploaded}
                      isImage={isImage}
                      isPdf={isPdf}
                      pendingName={pendingDocs[doc.key]?.name}
                      onUpload={(file) => handleUploadDoc(doc.key, file)}
                      onDelete={() => handleDeleteDoc(doc.key)}
                    />
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setActiveTab("salary"); scrollToTopOfPage(); }}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back: Salary Details
            </Button>
            <Button
              type="button"
              onClick={() => { setActiveTab("summary"); scrollToTopOfPage(); }}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
            >
              Next: Review & Submit
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Summary Tab */}
      {activeTab === "summary" && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-800">Review Employee Information</h2>
          <p className="text-gray-600">Please review all details before {employee?.id ? "updating" : "creating"} the employee</p>

          {/* Personal Summary */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-lg">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Full Name</Label>
                  <p className="text-gray-900 mt-1">{[formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(" ")}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Employee ID</Label>
                  <p className="text-gray-900 mt-1 font-mono">{formData.employeeID}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Email</Label>
                  <p className="text-gray-900 mt-1">{formData.email}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Phone</Label>
                  <p className="text-gray-900 mt-1">{formData.phone || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Department</Label>
                  <p className="text-gray-900 mt-1">{getDepartmentName(formData.department_id)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Role</Label>
                  <p className="text-gray-900 mt-1 capitalize">{formData.role}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Designation</Label>
                  <p className="text-gray-900 mt-1">{getDesignationName(formData.designation_id)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Date of Joining</Label>
                  <p className="text-gray-900 mt-1">{formData.hire_date}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Date of Birth</Label>
                  <p className="text-gray-900 mt-1">{formData.date_of_birth || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Mode of Working</Label>
                  <p className="text-gray-900 mt-1 capitalize">{formData.modeOfWorking}</p>
                </div>
                {formData.manager_id && (
                  <div>
                    <Label className="text-xs font-semibold text-gray-500">Reporting Person</Label>
                    <p className="text-gray-900 mt-1">{getManagerName(formData.manager_id)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Address Summary */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="text-lg">Address Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Current Address</h4>
                  <p className="text-gray-600">{formData.current_address1}</p>
                  {formData.current_address2 && <p className="text-gray-600">{formData.current_address2}</p>}
                  <p className="text-gray-600">{formData.current_city}, {getStateName(formData.current_state)} - {formData.current_pin}</p>
                  <p className="text-gray-600">{getCountryName(formData.country)}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Permanent Address</h4>
                  {formData.sameAs ? (
                    <p className="text-gray-500 italic">Same as current address</p>
                  ) : (
                    <>
                      <p className="text-gray-600">{formData.permanent_address1}</p>
                      {formData.permanent_address2 && <p className="text-gray-600">{formData.permanent_address2}</p>}
                      <p className="text-gray-600">{formData.permanent_city}, {getStateName(formData.permanent_state)} - {formData.permanent_pin}</p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Salary Summary - Show all records */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="text-lg">Salary Details</CardTitle>
              <CardDescription>Tax Regime: {formData.taxRegime === 'new' ? 'New Regime' : 'Old Regime'}</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {salaryRecords.map((record, idx) => {
                const details = calculateSalaryDetailsForRecord(record);
                return (
                  <div key={record.id} className={`${idx > 0 ? 'mt-6 pt-6 border-t border-gray-200' : ''}`}>
                    <div className="mb-3 flex justify-between items-center">
                      <h4 className="font-semibold text-gray-700">Salary Component {idx + 1}</h4>
                      <div className="flex gap-3 text-xs">
                        <span className="text-gray-500">Effective: {record.effectiveDate}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${record.status === 'active' ? 'bg-green-100 text-green-700' : record.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                          {record.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-3">Earnings</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Basic Salary:</span><span>₹{fmt(record.basicSalary)}</span></div>
                          <div className="flex justify-between"><span>HRA:</span><span>₹{fmt(record.hra)}</span></div>
                          <div className="flex justify-between"><span>Conveyance:</span><span>₹{fmt(record.conveyance)}</span></div>
                          <div className="flex justify-between"><span>Medical Allowance:</span><span>₹{fmt(record.medicalallowance)}</span></div>
                          <div className="flex justify-between"><span>Special Allowance:</span><span>₹{fmt(record.allowance)}</span></div>
                          <div className="flex justify-between"><span>LTA:</span><span>₹{fmt(record.lta)}</span></div>
                          {record.others > 0 && <div className="flex justify-between"><span>Others:</span><span>₹{fmt(record.others)}</span></div>}
                          {record.bonusAmount > 0 && <div className="flex justify-between"><span>Bonus:</span><span>₹{fmt(record.bonusAmount)}</span></div>}
                          <div className="flex justify-between pt-2 border-t mt-2 font-semibold"><span>Gross Monthly:</span><span>₹{fmt(details.gross)}</span></div>
                        </div>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-3">Deductions</h4>
                        <div className="space-y-2 text-sm">
                          {pfApplicable && <div className="flex justify-between"><span>EPF (EE):</span><span>₹{fmt(details.pfEE)}</span></div>}
                          {esicApplicable && details.esicEE > 0 && <div className="flex justify-between"><span>ESIC (EE):</span><span>₹{fmt(details.esicEE)}</span></div>}
                          {ptApplicable && <div className="flex justify-between"><span>Professional Tax:</span><span>₹{fmt(details.pt)}</span></div>}
                          {lwfApplicable && <div className="flex justify-between"><span>LWF (EE):</span><span>₹{fmt(details.lwfEE)}</span></div>}
                          {formData.tdsApplicable && <div className="flex justify-between"><span>TDS:</span><span>₹{fmt(details.tds)}</span></div>}
                          <div className="flex justify-between pt-2 border-t mt-2 font-semibold"><span>Total Deductions:</span><span>₹{fmt(details.totDed)}</span></div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-blue-800">Net Pay (Take Home)</span>
                        <span className="text-2xl font-bold text-blue-600">₹{fmt(details.net)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back: Salary Details
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {employee?.id ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {employee?.id ? "Update Employee" : "Create Employee"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      <Dialog open={addDepartmentModal} onOpenChange={setAddDepartmentModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Add New Department</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="departmentName">Department Name</Label>
            <Input
              id="departmentName"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setAddDepartmentModal(false)} variant="outline">
              Close
            </Button>
            <Button onClick={handleAddDepartment}>
              Add Department
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Designation Modal */}
      <Dialog open={addDesignationModal} onOpenChange={setAddDesignationModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Add New Designation</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="designationName">Designation Name</Label>
            <Input
              id="designationName"
              value={designationName}
              onChange={(e) => setDesignationName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setAddDesignationModal(false)} variant="outline">
              Close
            </Button>
            <Button onClick={handleAddDesignation}>
              Add Designation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Office-location missing popup — shown when the user picks
          "In Office" mode but the configured office row has no
          latitude / longitude / radius yet. They're redirected to the
          Office settings page to fill those in; once saved, picking
          "In Office" succeeds silently. */}
      <Dialog
        open={officeLocationMissingOpen}
        onOpenChange={setOfficeLocationMissingOpen}
      >
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-600" />
              Set office location first
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              In-office attendance needs the office's{" "}
              <span className="font-semibold">latitude</span>,{" "}
              <span className="font-semibold">longitude</span>, and{" "}
              <span className="font-semibold">radius</span> to verify
              employees are on premises.
            </p>
            <p>
              Please add these in Office Information first.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOfficeLocationMissingOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setOfficeLocationMissingOpen(false);
                navigate("/office");
              }}
            >
              Open Office Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}

